import { ButtonType, ButtonWidget } from '@tableau/widgets';
import * as React from 'react';

import { PageHeaderStyles } from '~src/app/layout/PageHeaderStyles';
import { Messages } from '~src/codegen/Localize';
import { imgPath } from '~src/common/utilities/ImgUrl';
import { openReactConfirmationDialog } from '~src/commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { FieldArrayRenderProps, FormikContextType } from '~src/settings/FormikWrapperForStubbing';
import { IRemoteQueryAgent } from '~src/settings/types/ISiteAdminBridgeSettings';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { L10n } from '~src/ts/L10n/L10n';
import { ToasterService, ToastType } from '~src/ts/services/ToasterService';
import { ServerUsers } from '~src/userManagement/services/resources/ServerUsers';

import { SiteBridgeSettingsForSiteAdminProps } from '../../../bridge-settings/BridgeSiteSettings';

export type BridgeRemoteAgentsDeleteButtonProps = Readonly<{
  agent: IRemoteQueryAgent;
  index: number;
  arrayHelpers: FieldArrayRenderProps;
  formik: FormikContextType<SiteBridgeSettingsForSiteAdminProps>;
  isDefaultPool: boolean;
  poolTestId: string;
  refreshEdgePools: () => void;
}>;

export function BridgeRemoteAgentsDeleteButton(props: BridgeRemoteAgentsDeleteButtonProps) {
  const { isDefaultPool, poolTestId } = props;
  return (
    <span className={'tb-data-grid-icon tb-padded'} data-tb-test-id={'agent-delete-cell' + poolTestId}>
      <ButtonWidget
        title={Messages.deleteMessage()}
        buttonType={isDefaultPool ? ButtonType.Icon : ButtonType.LowEmphasis}
        testId={'delete-remote-agent-button' + poolTestId}
        onClick={() => openDeleteRemoteAgentDialog(props)}
        style={{ alignItems: 'stretch' }}
      >
        {isDefaultPool ?
          <img src={imgPath('trash_14px_small.svg')} alt={Messages.deleteMessage()} />
          :
          <span>
            <img src={imgPath('trash_14px_small.svg')} style={PageHeaderStyles.iconForTextButton} alt={Messages.deleteMessage()} />
            {!isDefaultPool && Messages.deleteMessage()}
          </span>
        }
      </ButtonWidget>
    </span>
  );
}

function openDeleteRemoteAgentDialog(props: BridgeRemoteAgentsDeleteButtonProps) {
  const { agent, index, arrayHelpers, formik, refreshEdgePools } = props;
  const dialogInstance = openReactConfirmationDialog({
    title: Messages.deleteMessage(),
    message: Messages.confirmDeleteRemoteAgent({ NAME: L10n.elideIfLong(agent.agentName) }),
    paramsForAction: { index: index, arrayHelpers: arrayHelpers },
    actionName: Messages.deleteMessage(),
    action: ({ index, arrayHelpers }) => {
      // TODO - ndepass 9/22/21 - Story 1297102: [BridgeSiteSettings] Create Multi-Pool table
      // Remove formik when edgeMultiPoolEnabled FF is removed
      if (FeatureFlags.edgeMultiPoolEnabled) {
        const remoteAgents = formik.values.settings.siteAdminBridgeSettings.remoteAgentSettings.agents;
        index = remoteAgents.findIndex((formikAgent: IRemoteQueryAgent) => formikAgent.agentName === agent.agentName);
      }
      arrayHelpers.remove(index);
      ServerUsers.instance.deleteUserRemoteAgents(agent.ownerId, [agent.deviceId])
        .then((result: IResult) => {
          if (!result || (result.errors && result.errors.length)) {
            ToasterService.getInstance().pop({ type: ToastType.Error, text: Messages.errorUnknown() });
            arrayHelpers.insert(index, agent);
          } else {
            ToasterService.instance.pop({ type: ToastType.Success, text: Messages.deleteConnectedAgentSuccess() });
            formik.submitForm();
            FeatureFlags.edgeMultiPoolEnabled && refreshEdgePools();
          }
        });
      return Promise.resolve();
    },
    isActionDelete: true,
    dialogWidth: TabDialogWidth.width375px
  });

  return Promise.resolve(dialogInstance.result);
}